// frontend/src/services/aiEngineService.ts
/**
 * Pensado por: Alcélio Gomes
 * Codificado em coautoria: Claude Code
 * 🚀 NeuroAgentes
 * 
 * AUDITORIA: Cliente TypeScript para consumir APIs do AI Engine.
 * Fornece métodos para análise de sentimento, detecção de intent e entidades.
 * Arquitetura: Frontend → Node.js → AI Engine Python
 */

import secureApi from './api';
import { API_ENDPOINTS } from '../config/api.config';
import {
  SentimentRequest,
  SentimentAnalysis,
  IntentRequest,
  IntentDetection,
  EntitiesRequest,
  EntityExtraction,
  CompleteTextAnalysis,
  ConfidenceRequest,
  ConfidenceCalculation,
  EscalationCheck,
  ConfidenceHealth,
  LearningAnalysisRequest,
  LearningPattern,
  LearningInsight,
  LearningReport,
  LearningReportRequest,
  ModelUpdateRequest,
  LearningHealth,
  CompleteAnalysisRequest,
  CompleteAnalysis,
  AIEngineHealth,
  AIEngineStats,
  AIEngineResponse,
  AIEngineMetrics
} from '../types/aiEngine';

class AIEngineService {
  // ===============================================
  // CORE AI SERVICES
  // ===============================================

  /**
   * Análise de sentimento usando BERT PT-BR
   */
  async analyzeSentiment(request: SentimentRequest): Promise<SentimentAnalysis> {
    const response = await secureApi.post<AIEngineResponse<SentimentAnalysis>>(
      API_ENDPOINTS.aiEngine.sentiment,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Sentiment analysis failed');
    }

    return response.data.data!;
  }

  /**
   * Detecção de intenções usando classificadores neurais
   */
  async detectIntent(request: IntentRequest): Promise<IntentDetection> {
    const response = await secureApi.post<AIEngineResponse<IntentDetection>>(
      API_ENDPOINTS.aiEngine.intent,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Intent detection failed');
    }

    return response.data.data!;
  }

  /**
   * Extração de entidades usando spaCy PT-BR
   */
  async extractEntities(request: EntitiesRequest): Promise<EntityExtraction> {
    const response = await secureApi.post<AIEngineResponse<EntityExtraction>>(
      API_ENDPOINTS.aiEngine.entities,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Entity extraction failed');
    }

    return response.data.data!;
  }

  /**
   * Análise completa de texto (sentimento + intenção + entidades)
   */
  async analyzeText(text: string, projectId: string, context?: Record<string, any>): Promise<CompleteTextAnalysis> {
    const response = await secureApi.post<AIEngineResponse<CompleteTextAnalysis>>(
      API_ENDPOINTS.aiEngine.analyzeText,
      { text, project_id: projectId, context }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Text analysis failed');
    }

    return response.data.data!;
  }

  // ===============================================
  // CONFIDENCE SERVICE
  // ===============================================

  /**
   * Calcular confidence score
   */
  async calculateConfidence(request: ConfidenceRequest): Promise<ConfidenceCalculation> {
    const response = await secureApi.post<AIEngineResponse<ConfidenceCalculation>>(
      API_ENDPOINTS.aiEngine.confidence.calculate,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Confidence calculation failed');
    }

    return response.data.data!;
  }

  /**
   * Verificar se deve escalar baseado em confidence
   */
  async shouldEscalate(request: ConfidenceRequest): Promise<EscalationCheck> {
    const response = await secureApi.post<AIEngineResponse<EscalationCheck>>(
      API_ENDPOINTS.aiEngine.confidence.shouldEscalate,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Escalation check failed');
    }

    return response.data.data!;
  }

  /**
   * Health check do Confidence Service
   */
  async getConfidenceHealth(): Promise<ConfidenceHealth> {
    const response = await secureApi.get<AIEngineResponse<ConfidenceHealth>>(
      API_ENDPOINTS.aiEngine.confidence.health
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Confidence health check failed');
    }

    return response.data.data!;
  }

  // ===============================================
  // LEARNING SERVICE
  // ===============================================

  /**
   * Analisar interação para extrair padrões de aprendizado
   */
  async analyzeInteractionPattern(request: LearningAnalysisRequest): Promise<LearningPattern> {
    const response = await secureApi.post<AIEngineResponse<LearningPattern>>(
      API_ENDPOINTS.aiEngine.learning.analyzeInteraction,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Learning pattern analysis failed');
    }

    return response.data.data!;
  }

  /**
   * Obter insights de aprendizado para agente
   */
  async getLearningInsights(
    agentId: string, 
    projectId: string, 
    minFrequency: number = 5
  ): Promise<LearningInsight[]> {
    const response = await secureApi.get<AIEngineResponse<LearningInsight[]>>(
      API_ENDPOINTS.aiEngine.learning.insights(agentId, projectId),
      { params: { min_frequency: minFrequency } }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Learning insights retrieval failed');
    }

    return response.data.data!;
  }

  /**
   * Gerar relatório completo de aprendizado
   */
  async generateLearningReport(request: LearningReportRequest): Promise<LearningReport> {
    const response = await secureApi.post<AIEngineResponse<LearningReport>>(
      API_ENDPOINTS.aiEngine.learning.report,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Learning report generation failed');
    }

    return response.data.data!;
  }

  /**
   * Obter solicitações de atualização de modelo
   */
  async getModelUpdateRequests(agentId?: string, projectId?: string): Promise<ModelUpdateRequest[]> {
    const response = await secureApi.get<AIEngineResponse<ModelUpdateRequest[]>>(
      API_ENDPOINTS.aiEngine.learning.modelUpdateRequests,
      { params: { agent_id: agentId, project_id: projectId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Model update requests retrieval failed');
    }

    return response.data.data!;
  }

  /**
   * Solicitar atualização de modelo
   */
  async requestModelUpdate(request: {
    agent_id: string;
    project_id: string;
    update_type: 'incremental' | 'full_retrain' | 'parameter_tuning';
    priority: 'low' | 'medium' | 'high' | 'critical';
    reason: string;
    affected_patterns?: string[];
    scheduled_for?: string;
  }): Promise<{
    id: string;
    status: string;
    estimated_improvement: number;
    training_data_size: number;
    scheduled_for?: string;
  }> {
    const response = await secureApi.post<AIEngineResponse<any>>(
      API_ENDPOINTS.aiEngine.learning.requestModelUpdate,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Model update request failed');
    }

    return response.data.data!;
  }

  /**
   * Health check do Learning Service
   */
  async getLearningHealth(): Promise<LearningHealth> {
    const response = await secureApi.get<AIEngineResponse<LearningHealth>>(
      API_ENDPOINTS.aiEngine.learning.health
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Learning health check failed');
    }

    return response.data.data!;
  }

  // ===============================================
  // INTERNAL AGENT
  // ===============================================

  // REMOVED: Internal Agent methods - migrated to LEX

  /**
   * Limpar memória de conversa de um usuário específico
   */
  async clearUserMemory(userId: string): Promise<any> {
    const response = await secureApi.delete<AIEngineResponse<any>>(
      `/api/services/ai-engine/agent/memory/${userId}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to clear user memory');
    }

    return response.data.data!;
  }

  /**
   * Obter estatísticas de memória dos usuários
   */
  async getMemoryStats(): Promise<any> {
    const response = await secureApi.get<AIEngineResponse<any>>(
      '/api/services/ai-engine/agent/memory/stats'
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get memory stats');
    }

    return response.data.data!;
  }

  /**
   * Obter histórico de conversas de um usuário
   */
  async getUserConversationHistory(userId: string, limit: number = 10, days: number = 30): Promise<any> {
    const response = await secureApi.get<AIEngineResponse<any>>(
      `/api/services/ai-engine/agent/conversations/${userId}`,
      { params: { limit, days } }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get conversation history');
    }

    return response.data.data!;
  }

  // ===============================================
  // COMPLETE ANALYSIS
  // ===============================================

  /**
   * Análise completa: confidence + learning + text analysis
   */
  async performCompleteAnalysis(request: CompleteAnalysisRequest): Promise<CompleteAnalysis> {
    const response = await secureApi.post<AIEngineResponse<CompleteAnalysis>>(
      API_ENDPOINTS.aiEngine.analyzeComplete,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Complete analysis failed');
    }

    return response.data.data!;
  }

  // ===============================================
  // HEALTH & STATS
  // ===============================================

  /**
   * Health check geral do AI Engine
   */
  async checkHealth(): Promise<AIEngineHealth> {
    const response = await secureApi.get<AIEngineResponse<AIEngineHealth>>(
      API_ENDPOINTS.aiEngine.health
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'AI Engine health check failed');
    }

    return response.data.data!;
  }

  /**
   * Obter estatísticas do AI Engine
   */
  async getStats(): Promise<AIEngineStats> {
    const response = await secureApi.get<AIEngineResponse<AIEngineStats>>(
      API_ENDPOINTS.aiEngine.stats
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'AI Engine stats failed');
    }

    return response.data.data!;
  }

  // ===============================================
  // CONVENIENCE METHODS
  // ===============================================

  /**
   * Obter métricas consolidadas do AI Engine para dashboard
   */
  async getDashboardMetrics(projectId?: string): Promise<AIEngineMetrics> {
    try {
      // Executar múltiplas chamadas em paralelo para compilar métricas
      const [health, stats, confidenceHealth, learningHealth] = await Promise.all([
        this.checkHealth(),
        this.getStats(),
        this.getConfidenceHealth().catch(() => null),
        this.getLearningHealth().catch(() => null)
      ]);

      // Compilar métricas a partir dos dados obtidos
      const metrics: AIEngineMetrics = {
        sentiment_analysis: {
          total_requests: 0,
          avg_processing_time: 0,
          accuracy_score: 0.85, // Mock data - seria calculado
          distribution: {
            positive: 45,
            negative: 25,
            neutral: 30
          }
        },
        intent_detection: {
          total_requests: 0,
          avg_processing_time: 0,
          accuracy_score: 0.92, // Mock data - seria calculado
          top_intents: [
            { intent: 'product_inquiry', frequency: 156, avg_confidence: 0.89 },
            { intent: 'support_request', frequency: 134, avg_confidence: 0.87 },
            { intent: 'order_status', frequency: 98, avg_confidence: 0.93 }
          ]
        },
        confidence_scores: {
          avg_confidence: 0.78,
          escalation_rate: 0.12,
          improvement_trend: 0.05,
          distribution: {
            high: 45,
            medium: 40,
            low: 15
          }
        },
        learning_insights: {
          total_patterns: 0,
          successful_patterns: 0,
          avg_success_rate: 0.82,
          model_update_requests: 0,
          recent_improvements: []
        },
        system_health: {
          ai_engine_status: health?.status === 'healthy' ? 'healthy' : 'degraded',
          confidence_service_status: confidenceHealth?.status === 'healthy' ? 'healthy' : 'degraded',
          learning_service_status: learningHealth?.status === 'healthy' ? 'healthy' : 'degraded',
          overall_performance: stats?.isHealthy ? 0.95 : 0.5
        }
      };

      return metrics;
    } catch (error) {
      console.error('Error getting dashboard metrics:', error);
      throw new Error('Failed to get dashboard metrics');
    }
  }

  /**
   * Verificar se AI Engine está operacional
   */
  async isOperational(): Promise<boolean> {
    try {
      const health = await this.checkHealth();
      return health.status === 'healthy';
    } catch (error) {
      console.error('AI Engine operational check failed:', error);
      return false;
    }
  }

  /**
   * Obter resumo do status de todos os serviços
   */
  async getServicesStatus(): Promise<{
    aiEngine: 'healthy' | 'degraded' | 'offline';
    confidence: 'healthy' | 'degraded' | 'offline';
    learning: 'healthy' | 'degraded' | 'offline';
    overall: 'healthy' | 'degraded' | 'offline';
  }> {
    try {
      const [health, confidenceHealth, learningHealth] = await Promise.allSettled([
        this.checkHealth(),
        this.getConfidenceHealth(),
        this.getLearningHealth()
      ]);

      const aiEngineStatus = health.status === 'fulfilled' ? 'healthy' : 'offline';
      const confidenceStatus = confidenceHealth.status === 'fulfilled' ? 'healthy' : 'offline';
      const learningStatus = learningHealth.status === 'fulfilled' ? 'healthy' : 'offline';

      const allHealthy = aiEngineStatus === 'healthy' && confidenceStatus === 'healthy' && learningStatus === 'healthy';
      const anyHealthy = aiEngineStatus === 'healthy' || confidenceStatus === 'healthy' || learningStatus === 'healthy';
      
      const overall = allHealthy ? 'healthy' : anyHealthy ? 'degraded' : 'offline';

      return {
        aiEngine: aiEngineStatus,
        confidence: confidenceStatus,
        learning: learningStatus,
        overall
      };
    } catch (error) {
      console.error('Services status check failed:', error);
      return {
        aiEngine: 'offline',
        confidence: 'offline',
        learning: 'offline',
        overall: 'offline'
      };
    }
  }
}

// Singleton instance
export const aiEngineService = new AIEngineService();
export default aiEngineService;